import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Users, Megaphone, Handshake, ArrowRight, CheckCircle,
  Mail, Calendar, Send, Loader2, MapPin, Clock, Phone,
  HandHeart, Sparkles, ImageIcon
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Involved = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: 'Volunteer Inquiry', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/public/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || 'Volunteer Inquiry',
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToVolunteer = () => {
    document.getElementById('volunteer-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const ways = [
    {
      icon: Heart, title: 'Donate',
      description: 'Your financial support helps us provide essential aid to those in need. Every dollar makes a difference.',
      cta: 'Make a Donation', link: '/donate',
    },
    {
      icon: Users, title: 'Volunteer',
      description: 'Join our team of dedicated volunteers. Help with food distribution, care packages, events, and more.',
      cta: 'Sign Up to Volunteer', action: scrollToVolunteer,
    },
    {
      icon: Megaphone, title: 'Spread the Word',
      description: 'Share our mission with your friends, family, and community. Awareness helps us reach more people.',
      cta: 'Share Our Mission', link: '/about',
    },
    {
      icon: Handshake, title: 'Partner With Us',
      description: 'Organizations, businesses, and community groups can partner with us for greater impact.',
      cta: 'Become a Partner', link: '/contact',
    },
  ];

  const volunteerRoles = [
    { title: 'Food Distribution', time: 'Weekly, 3-4 hours', description: 'Help sort, pack, and distribute food to families in need.' },
    { title: 'Care Package Assembly', time: 'Bi-weekly, 2-3 hours', description: 'Assemble hygiene kits and care packages for distribution.' },
    { title: 'Event Support', time: 'As needed', description: 'Help with fundraising events, community outreach, and awareness campaigns.' },
    { title: 'Administrative Support', time: 'Flexible', description: 'Assist with data entry, communications, and organizational tasks.' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="bg-[#BA1826] py-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Get Involved</h1>
      </div>

      {/* Ways to Help */}
      <section className="py-14 px-6 bg-white" data-testid="ways-to-help">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-3">Make an Impact</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">Ways You Can Help</h2>
            <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
              Whether you give your time, resources, or voice, your contribution matters.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {ways.map((way, index) => (
              <div key={way.title} className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-8 hover:shadow-md transition-shadow"
                data-testid={`way-card-${way.title.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="w-12 h-12 bg-[#FEE2E2] rounded-lg flex items-center justify-center mb-5">
                  <way.icon className="w-6 h-6 text-[#BA1826]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">{way.title}</h3>
                <p className="text-sm text-[#4B5563] mb-5 leading-relaxed">{way.description}</p>
                {way.action ? (
                  <button onClick={way.action} className="btn-primary text-sm inline-flex items-center gap-2" data-testid={`way-cta-${index}`}>
                    {way.cta} <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <Link to={way.link} className="btn-primary text-sm inline-flex items-center gap-2" data-testid={`way-cta-${index}`}>
                    {way.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      <section id="volunteer-section" className="py-14 px-6 bg-[#F8F9FA]" data-testid="volunteer-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-3">Join Our Team</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">Volunteer With Us</h2>
            <p className="text-sm text-[#4B5563] max-w-lg mx-auto leading-relaxed">
              Whether you can give an hour or a day, your time and skills help families receive the
              support they need — delivered with dignity and care.
            </p>
          </div>

          {/* Why Volunteer */}
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
            {[
              { icon: HandHeart, title: 'Direct Impact', desc: 'Hands-on work that changes lives' },
              { icon: Users, title: 'Community', desc: 'Join a team that cares deeply' },
              { icon: Sparkles, title: 'Growth', desc: 'Build skills while giving back' },
            ].map((item) => (
              <div key={item.title} className="text-center p-4 bg-white border border-[#E5E7EB] rounded-xl">
                <item.icon className="w-6 h-6 text-[#BA1826] mx-auto mb-2" />
                <h3 className="font-heading text-sm font-semibold mb-1">{item.title}</h3>
                <p className="text-[10px] text-[#4B5563]">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Volunteer Opportunities + Contact Form */}
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left: Opportunities */}
            <div>
              <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-5">Current Volunteer Opportunities</h3>
              <div className="space-y-3">
                {volunteerRoles.map((role) => (
                  <div key={role.title} className="bg-white border border-[#E5E7EB] rounded-lg p-5"
                    data-testid={`volunteer-role-${role.title.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-heading font-semibold text-sm text-[#0A0A0A]">{role.title}</h4>
                      <span className="text-[10px] font-medium text-[#4B5563] bg-[#F8F9FA] px-2 py-1 rounded flex items-center gap-1 flex-shrink-0">
                        <Calendar className="w-3 h-3" /> {role.time}
                      </span>
                    </div>
                    <p className="text-xs text-[#4B5563] leading-relaxed">{role.description}</p>
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="mt-6 bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="contact-info">
                <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-4">Get in Touch</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                    <Mail className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                    <span>novenahfoundation@gmail.com</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                    <Phone className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                    <span>(347) 555-0199</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                    <MapPin className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                    <span>New York, NY</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#4B5563]">
                    <Clock className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                    <span>We typically respond within 24–48 hours</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Message Form */}
            <div>
              {submitted ? (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="volunteer-success">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">Thank You!</h3>
                  <p className="text-sm text-[#4B5563] mb-5">
                    Your message has been sent. Our team will review your information and reach out within 24–48 hours to discuss next steps.
                  </p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: 'Volunteer Inquiry', message: '' }); }}
                    className="text-sm text-[#BA1826] font-semibold hover:underline" data-testid="send-another-btn">
                    Send another message
                  </button>
                </div>
              ) : (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="volunteer-form-container">
                  <h3 className="font-heading text-lg font-bold text-[#0A0A0A] mb-1">Send Us a Message</h3>
                  <p className="text-xs text-[#4B5563] mb-5">Interested in volunteering? Fill out the form and we'll be in touch.</p>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg mb-4" data-testid="form-error">{error}</div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="volunteer-form">
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1">Name *</label>
                      <input type="text" required value={form.name} onChange={(e) => update('name', e.target.value)}
                        placeholder="Your full name"
                        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
                        data-testid="form-name-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1">Email *</label>
                      <input type="email" required value={form.email} onChange={(e) => update('email', e.target.value)}
                        placeholder="you@example.com"
                        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
                        data-testid="form-email-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1">Subject</label>
                      <input type="text" value={form.subject} onChange={(e) => update('subject', e.target.value)}
                        placeholder="e.g., Volunteer Inquiry, Partnership, General Question"
                        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none"
                        data-testid="form-subject-input" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#4B5563] mb-1">Message *</label>
                      <textarea required value={form.message} onChange={(e) => update('message', e.target.value)}
                        placeholder="Tell us how you'd like to help, your availability, skills, or any questions..."
                        rows={5} maxLength={2000}
                        className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-lg text-sm focus:border-[#BA1826] focus:outline-none resize-none"
                        data-testid="form-message-input" />
                    </div>
                    <button type="submit" disabled={submitting || !form.name.trim() || !form.email.trim() || !form.message.trim()}
                      className="btn-primary w-full py-2.5 text-sm disabled:opacity-50" data-testid="form-submit-btn">
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Message</>}
                    </button>
                    <p className="text-[10px] text-[#9CA3AF] text-center">
                      We respect your privacy. Your information will only be used for volunteer coordination.
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-14 px-6 bg-white" data-testid="partnership-section">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-3">Corporate & Community Partners</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">Partner With Novena</h2>
          <p className="text-sm text-[#4B5563] leading-relaxed mb-8 max-w-lg mx-auto">
            We welcome partnerships with businesses, organizations, and community groups
            who share our commitment to serving those in need with dignity and compassion.
          </p>
          <div className="grid md:grid-cols-3 gap-4 mb-10 text-left max-w-xl mx-auto">
            {['Corporate Sponsorships', 'In-Kind Donations', 'Employee Volunteer Programs',
              'Event Partnerships', 'Community Collaborations', 'Matching Gift Programs'].map((item) => (
              <div key={item} className="flex items-center gap-2" data-testid={`partner-item-${item.toLowerCase().replace(/\s/g, '-')}`}>
                <CheckCircle className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                <span className="text-sm text-[#4B5563]">{item}</span>
              </div>
            ))}
          </div>
          <Link to="/contact" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="partnership-contact-btn">
            Discuss Partnership <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-6 bg-[#0A0A0A]" data-testid="involved-cta">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-3">Every Action Counts</h2>
          <p className="text-sm text-gray-400 max-w-lg mx-auto mb-6">
            Whether you give $10 or 10 hours, your contribution helps us serve more
            families with dignity and compassion.
          </p>
          <Link to="/donate" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="involved-donate-btn">
            Make Your Impact Today <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Involved;
